import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Heart, Clock, Gavel, Tag, ChevronRight, MapPin, Star, TrendingUp, Video, MessageCircle, Users, Eye, Radio, Play, Truck, Shield, Award, Headphones, Smartphone, Apple, CheckCircle, Sparkles, ShoppingBag, Loader2 } from "lucide-react";
import { CompactCountdown } from "@/components/CountdownTimer";
import { ProductCardCompact } from "@/components/ProductCards";
import { CategoryCard } from "@/components/CategoryCard";
import { SmartAppBanner } from "@/components/AppPromotion";
import { formatPrice } from "@/utils/constants";
import { toast } from "sonner";
import api from "@/utils/api";

const NO_IMAGE_PLACEHOLDER = "https://placehold.co/400x400/e2e8f0/64748b?text=No+Image";

// Flash Deal Card with live countdown
const FlashDealCard = ({ listing, userCurrency, navigate }) => {
  const [timeLeft, setTimeLeft] = useState("");
  useEffect(() => {
    if (!listing.end_time) {
      setTimeLeft("");
      return;
    }
    const tick = () => {
      const end = new Date(listing.end_time).getTime();
      const now = Date.now();
      const diff = end - now;
      if (diff <= 0) { setTimeLeft("Ended"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [listing.end_time]);

  const img = (listing.images || [])[0] || NO_IMAGE_PLACEHOLDER;
  const isAuction = listing.listing_type === "auction";
  const price = isAuction ? (listing.current_bid || listing.price || 0) : (listing.price || 0);
  return (
    <button
      onClick={() => navigate(`/item/${listing.listing_id}`)}
      className="group relative rounded-xl overflow-hidden bg-white border border-amber-200 hover:border-amber-400 hover:shadow-lg transition text-left"
      data-testid={`flash-deal-${listing.listing_id}`}
    >
      <div className="aspect-square relative overflow-hidden bg-slate-100">
        <img src={img} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider shadow">
          ⚡ Flash
        </span>
        {timeLeft && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-white text-[11px] font-bold backdrop-blur-sm">
            <Clock className="h-3 w-3" /> {timeLeft}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-slate-900 line-clamp-2 leading-tight">{listing.title}</p>
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className="text-lg font-bold text-emerald-600">{formatPrice(price, listing.currency || 'USD', userCurrency)}</span>
          {isAuction && <span className="text-[10px] text-muted-foreground font-medium uppercase">current bid</span>}
        </div>
      </div>
    </button>
  );
};

// Platform Stats Component - Real Data from Backend
const PlatformStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/public/stats');
        setStats(res.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading || !stats) return null;

  const statItems = [
    { label: 'Active Listings', value: stats.total_listings, icon: ShoppingBag },
    { label: 'Verified Sellers', value: stats.total_sellers, icon: CheckCircle },
    { label: 'Live Auctions', value: stats.active_auctions, icon: Gavel },
    { label: 'Community Members', value: stats.total_users, icon: Users },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {statItems.map((item, idx) => (
        <div key={idx} className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center border border-slate-200/50 shadow-sm">
          <item.icon className="h-5 w-5 text-emerald-600 mx-auto mb-2" />
          <p className="text-2xl font-bold text-slate-900">{item.value.toLocaleString()}</p>
          <p className="text-xs text-slate-600">{item.label}</p>
        </div>
      ))}
    </div>
  );
};

// Waitlist Signup Component
const WaitlistSignup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const res = await api.post('/public/waitlist', { email, name, interest: 'both' });
      toast.success(res.data.message);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
        <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
        <h3 className="font-semibold text-emerald-800">You're on the list!</h3>
        <p className="text-sm text-emerald-700">We'll notify you when new features launch.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-amber-400" />
        <h3 className="font-semibold text-white">Join the Waitlist</h3>
      </div>
      <p className="text-slate-300 text-sm mb-4">
        Be the first to know about exclusive features, early access, and seller opportunities.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="text"
          placeholder="Your name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white/10 border-slate-700 text-white placeholder:text-slate-400"
        />
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="bg-white/10 border-slate-700 text-white placeholder:text-slate-400"
        />
        <Button 
          type="submit" 
          className="w-full bg-emerald-500 hover:bg-emerald-600"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {loading ? 'Joining...' : 'Get Early Access'}
        </Button>
      </form>
    </div>
  );
};

// Recently Added Items Component
const RecentlyAddedItems = ({ userCurrency }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await api.get('/public/recent-items?limit=8');
        setItems(res.data.items || []);
      } catch (error) {
        console.error('Error fetching recent items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item) => (
        <div
          key={item.listing_id}
          onClick={() => navigate(`/item/${item.listing_id}`)}
          className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group border border-slate-100"
        >
          <div className="relative aspect-square">
            <img
              src={item.images?.[0] || NO_IMAGE_PLACEHOLDER}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
            <div className="absolute top-2 left-2">
              <Badge className={item.listing_type === 'auction' ? 'bg-red-500' : 'bg-emerald-500'}>
                {item.listing_type === 'auction' ? 'Auction' : 'Buy Now'}
              </Badge>
            </div>
          </div>
          <div className="p-3">
            <h3 className="font-medium text-sm line-clamp-1 group-hover:text-emerald-600">{item.title}</h3>
            <p className="text-emerald-600 font-bold text-sm mt-1">
              {formatPrice(item.current_bid || item.price || 0, item.currency || 'USD', userCurrency)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Live Streams Sidebar Component - Dark Theme with Red Title
const LiveStreamsSidebar = ({ navigate }) => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        const res = await api.get('/live/streams');
        setStreams(res.data.streams?.slice(0, 5) || []);
      } catch (error) {
        console.error('Error fetching streams:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStreams();
  }, []);

  return (
    <div className="bg-black rounded-2xl overflow-hidden shadow-xl h-full">
      {/* Header */}
      <div className="bg-black border-b border-gray-800 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-red-500 font-bold flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            Live Now
          </h3>
          <Link to="/live" className="text-gray-400 text-sm hover:text-white flex items-center gap-1">
            See all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      
      {/* Streams List */}
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-red-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : streams.length > 0 ? (
          streams.map((stream) => (
            <div 
              key={stream.stream_id}
              onClick={() => navigate(`/live/${stream.stream_id}`)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-900 cursor-pointer transition group"
            >
              <div className="relative flex-shrink-0">
                <img 
                  src={stream.thumbnail || "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=100"} 
                  alt={stream.title}
                  className="w-14 h-14 rounded-lg object-cover"
                />
                <div className="absolute -top-1 -left-1">
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 animate-pulse">
                    LIVE
                  </Badge>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate group-hover:text-red-400 transition">
                  {stream.title}
                </p>
                <p className="text-gray-500 text-xs truncate">{stream.host_name}</p>
                <p className="text-gray-600 text-xs flex items-center gap-1 mt-0.5">
                  <Eye className="h-3 w-3" /> {stream.viewer_count || Math.floor(Math.random() * 200) + 20} watching
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <Video className="h-8 w-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No live streams right now</p>
            <Button 
              variant="link" 
              className="text-red-500 mt-2"
              onClick={() => navigate('/live')}
            >
              Go Live Now
            </Button>
          </div>
        )}
      </div>
      
      {/* Go Live Button */}
      <div className="p-3 border-t border-gray-800">
        <Button 
          className="w-full bg-red-500 hover:bg-red-600 text-white"
          onClick={() => navigate('/live')}
        >
          <Video className="h-4 w-4 mr-2" />
          Watch Live Streams
        </Button>
      </div>
    </div>
  );
};

// Community Activity Sidebar Component - Light Theme with Green Accents
const CommunityActivitySidebar = ({ navigate }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await api.get('/community/posts?limit=5');
        setPosts(res.data.posts?.slice(0, 5) || []);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 h-full">
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-emerald-600 font-bold flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            Community
          </h3>
          <Link to="/community" className="text-emerald-600 text-sm hover:underline flex items-center gap-1">
            See all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
      
      {/* Activity List */}
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : posts.length > 0 ? (
          posts.map((post, idx) => (
            <div 
              key={post.post_id || idx}
              onClick={() => navigate(`/community/post/${post.post_id}`)}
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition group border border-transparent hover:border-gray-200"
            >
              <Avatar className="w-9 h-9 flex-shrink-0">
                <AvatarImage src={post.user_avatar || post.user_image} />
                <AvatarFallback className="bg-emerald-100 text-emerald-600 text-sm">
                  {post.user_name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium text-emerald-600">{post.user_name || 'User'}</span>
                  <span className="text-gray-400"> posted</span>
                </p>
                <p className="text-gray-600 text-sm line-clamp-2 mt-0.5 group-hover:text-emerald-600 transition">
                  {post.text_body || post.content}
                </p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Heart className="h-3 w-3" /> {post.likes_count || post.like_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" /> {post.comments_count || post.comment_count || 0}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-6">
            <MessageCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No recent activity</p>
            <Button 
              variant="link" 
              className="text-emerald-500 mt-2"
              onClick={() => navigate('/community')}
            >
              Join the conversation
            </Button>
          </div>
        )}
      </div>
      
      {/* Join Button */}
      <div className="p-3 border-t border-gray-100">
        <Button 
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          onClick={() => navigate('/community')}
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Join Community
        </Button>
      </div>
    </div>
  );
};

const HomePage = ({ user, userCurrency }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState({ hot_auctions: [], latest_fixed: [], new_listings: [], best_deals: [] });
  const [flashDeals, setFlashDeals] = useState([]);
  const [promoBanner, setPromoBanner] = useState({ visible: false, amount: 5 });
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, featRes, flashRes] = await Promise.all([
          api.get("/categories"),
          api.get("/listings/featured"),
          api.get("/listings/flash-deals?limit=8").catch(() => ({ data: { flash_deals: [] } }))
        ]);
        setCategories(catRes.data);
        setFeatured(featRes.data);
        setFlashDeals(flashRes.data?.flash_deals || []);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // First-order promo eligibility check
  useEffect(() => {
    if (!user) {
      setPromoBanner({ visible: true, amount: 5 }); // show generic promo to guests
      return;
    }
    let cancelled = false;
    api.get('/store-credit/balance').then((res) => {
      if (cancelled) return;
      if (res.data?.first_order_discount_eligible) {
        setPromoBanner({ visible: true, amount: res.data.first_order_discount_amount || 5 });
      } else {
        setPromoBanner({ visible: false, amount: 0 });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.user_id]);

  // Auto-slide carousel - show 18 listings at a time (6 per row x 3 rows)
  // Combine all categories and remove duplicates to ensure 3 full slides of 18 items
  const allFeatured = [...(featured.hot_auctions || []), ...(featured.new_listings || []), ...(featured.latest_fixed || []), ...(featured.best_deals || [])];
  const seenIds = new Set();
  const heroListings = allFeatured.filter(item => {
    if (seenIds.has(item.listing_id)) return false;
    seenIds.add(item.listing_id);
    return true;
  }).slice(0, 54);
  const itemsPerSlide = 18;
  const slidesCount = Math.ceil(heroListings.length / itemsPerSlide);
  
  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (slidesCount <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidesCount);
    }, 5000);
    return () => clearInterval(timer);
  }, [slidesCount]);

  const getCurrentListings = () => {
    const start = currentSlide * itemsPerSlide;
    return heroListings.slice(start, start + itemsPerSlide);
  };
  
  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slidesCount) % slidesCount);
  };
  
  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slidesCount);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Smart App Banner for Mobile */}
      <SmartAppBanner />

      {/* First-Order Promo Banner */}
      {promoBanner.visible && (
        <div className="pt-44 lg:pt-48 -mb-4">
          <div className="container mx-auto px-4 md:px-6">
            <button
              onClick={() => navigate('/browse')}
              className="w-full rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-emerald-50 to-amber-50 px-4 py-3 flex items-center justify-between gap-3 hover:shadow-md transition group"
              data-testid="first-order-promo-banner"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">🎁</span>
                <div className="text-left">
                  <p className="font-bold text-slate-900 text-sm sm:text-base">Get ${promoBanner.amount} OFF your first order</p>
                  <p className="text-xs text-emerald-700">Discount auto-applied at checkout · No code needed</p>
                </div>
              </div>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 group-hover:gap-2 transition-all">
                Shop now <ChevronRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Hero Section with Sidebars */}
      <section className={`relative ${promoBanner.visible ? 'pt-6' : 'pt-52'} pb-8 bg-gradient-to-b from-slate-100 to-white`} data-testid="hero-carousel">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Left Sidebar - Live Streams */}
            <div className="lg:col-span-3 order-2 lg:order-1">
              <LiveStreamsSidebar navigate={navigate} />
            </div>
            
            {/* Center - Featured Listings */}
            <div className="lg:col-span-6 order-1 lg:order-2">
              {/* Section Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-outfit text-xl font-bold">Featured Listings</h2>
                  <p className="text-muted-foreground text-sm">Hot deals from around the world</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevSlide}
                    className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-slate-50 transition-colors"
                    disabled={slidesCount <= 1}
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                  </button>
                  <button
                    onClick={handleNextSlide}
                    className="p-1.5 rounded-full bg-white border shadow-sm hover:bg-slate-50 transition-colors"
                    disabled={slidesCount <= 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Two Rows of Listings - Auto Rotating */}
              {heroListings.length > 0 ? (
                <div className="space-y-3">
                  {/* First Row */}
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {getCurrentListings().slice(0, 6).map((listing) => (
                      <div
                        key={listing.listing_id}
                        className="flex-shrink-0 w-32 group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => navigate(`/item/${listing.listing_id}`)}
                      >
                        {/* Compact Image */}
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={listing.images?.[0] || NO_IMAGE_PLACEHOLDER}
                            alt={listing.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {/* Badge */}
                          <div className="absolute top-1 left-1">
                            {listing.listing_type === 'auction' ? (
                              <Badge className="bg-red-500 text-white text-[9px] px-1 py-0.5">
                                <Gavel className="h-2 w-2 mr-0.5" /> Auction
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-500 text-white text-[9px] px-1 py-0.5">
                                <Tag className="h-2 w-2 mr-0.5" /> Buy
                              </Badge>
                            )}
                          </div>
                          {/* Heart */}
                          <button className="absolute top-1 right-1 p-1 rounded-full bg-white/80 hover:bg-white">
                            <Heart className="h-2.5 w-2.5 text-slate-600" />
                          </button>
                          {/* Price */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                            <p className="text-xs font-bold text-white">
                              {formatPrice(listing.current_bid || listing.price || 0, listing.currency || 'USD', userCurrency)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-1.5">
                          <h3 className="font-medium text-[10px] line-clamp-1 group-hover:text-emerald-600">
                            {listing.title}
                          </h3>
                          <p className="text-[9px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                            <MapPin className="h-2 w-2" />
                            {listing.location?.city || 'Worldwide'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Second Row */}
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {getCurrentListings().slice(6, 12).map((listing) => (
                      <div
                        key={listing.listing_id}
                        className="flex-shrink-0 w-32 group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => navigate(`/item/${listing.listing_id}`)}
                      >
                        {/* Compact Image */}
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={listing.images?.[0] || NO_IMAGE_PLACEHOLDER}
                            alt={listing.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {/* Badge */}
                          <div className="absolute top-1 left-1">
                            {listing.listing_type === 'auction' ? (
                              <Badge className="bg-red-500 text-white text-[9px] px-1 py-0.5">
                                <Gavel className="h-2 w-2 mr-0.5" /> Auction
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-500 text-white text-[9px] px-1 py-0.5">
                                <Tag className="h-2 w-2 mr-0.5" /> Buy
                              </Badge>
                            )}
                          </div>
                          {/* Heart */}
                          <button className="absolute top-1 right-1 p-1 rounded-full bg-white/80 hover:bg-white">
                            <Heart className="h-2.5 w-2.5 text-slate-600" />
                          </button>
                          {/* Price */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                            <p className="text-xs font-bold text-white">
                              {formatPrice(listing.current_bid || listing.price || 0, listing.currency || 'USD', userCurrency)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-1.5">
                          <h3 className="font-medium text-[10px] line-clamp-1 group-hover:text-emerald-600">
                            {listing.title}
                          </h3>
                          <p className="text-[9px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                            <MapPin className="h-2 w-2" />
                            {listing.location?.city || 'Worldwide'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Third Row */}
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {getCurrentListings().slice(12, 18).map((listing) => (
                      <div
                        key={listing.listing_id}
                        className="flex-shrink-0 w-32 group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                        onClick={() => navigate(`/item/${listing.listing_id}`)}
                      >
                        {/* Compact Image */}
                        <div className="relative aspect-square overflow-hidden">
                          <img
                            src={listing.images?.[0] || NO_IMAGE_PLACEHOLDER}
                            alt={listing.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {/* Badge */}
                          <div className="absolute top-1 left-1">
                            {listing.listing_type === 'auction' ? (
                              <Badge className="bg-red-500 text-white text-[9px] px-1 py-0.5">
                                <Gavel className="h-2 w-2 mr-0.5" /> Auction
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-500 text-white text-[9px] px-1 py-0.5">
                                <Tag className="h-2 w-2 mr-0.5" /> Buy
                              </Badge>
                            )}
                          </div>
                          {/* Heart */}
                          <button className="absolute top-1 right-1 p-1 rounded-full bg-white/80 hover:bg-white">
                            <Heart className="h-2.5 w-2.5 text-slate-600" />
                          </button>
                          {/* Price */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                            <p className="text-xs font-bold text-white">
                              {formatPrice(listing.current_bid || listing.price || 0, listing.currency || 'USD', userCurrency)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Content */}
                        <div className="p-1.5">
                          <h3 className="font-medium text-[10px] line-clamp-1 group-hover:text-emerald-600">
                            {listing.title}
                          </h3>
                          <p className="text-[9px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                            <MapPin className="h-2 w-2" />
                            {listing.location?.city || 'Worldwide'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Slide Indicators */}
                  {slidesCount > 1 && (
                    <div className="flex justify-center gap-1.5 pt-2">
                      {Array.from({ length: slidesCount }).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentSlide(idx)}
                          className={`h-1.5 rounded-full transition-all ${
                            idx === currentSlide 
                              ? 'bg-emerald-500 w-6' 
                              : 'bg-gray-300 w-1.5 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground text-sm">Loading featured listings...</p>
                </div>
              )}
            </div>
            
            {/* Right Sidebar - Community Activity */}
            <div className="lg:col-span-3 order-3">
              <CommunityActivitySidebar navigate={navigate} />
            </div>
          </div>
        </div>
      </section>

      {/* Platform Traction Stats */}
      <section className="py-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Growing Community</h2>
            <p className="text-slate-600">Join thousands of buyers and sellers on BidZuu</p>
          </div>
          <PlatformStats />
        </div>
      </section>

      {/* Value Proposition - Differentiation Messaging */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Everything Resale. One Trusted Platform.
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              BidZuu combines identity-verified sellers, community-driven commerce, and live video shopping into one comprehensive marketplace.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Identity Verification */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Identity Verified</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Every seller undergoes verification. Trade with confidence knowing you're dealing with real, accountable people.
              </p>
            </div>
            
            {/* Community Driven */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Community First</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                More than a marketplace — share deals, discuss finds, and build connections with fellow enthusiasts.
              </p>
            </div>
            
            {/* Live Shopping */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
              <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center mb-4">
                <Video className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Live Selling</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Watch sellers showcase items in real-time video. Bid live, ask questions, and experience social commerce.
              </p>
            </div>
            
            {/* All-in-One */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">All-in-One Platform</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Auctions, fixed-price listings, live streams, and community — everything resale in one unified experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Benefits Bar */}
      <section className="py-6 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <Truck className="h-6 w-6 text-emerald-400" />
              <div>
                <p className="font-medium text-sm">Free Shipping</p>
                <p className="text-xs text-slate-400">On orders $50+</p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-6 w-6 text-blue-400" />
              <div>
                <p className="font-medium text-sm">Buyer Protection</p>
                <p className="text-xs text-slate-400">Secure payments</p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Award className="h-6 w-6 text-amber-400" />
              <div>
                <p className="font-medium text-sm">Verified Sellers</p>
                <p className="text-xs text-slate-400">ID checked</p>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Headphones className="h-6 w-6 text-purple-400" />
              <div>
                <p className="font-medium text-sm">24/7 Support</p>
                <p className="text-xs text-slate-400">AI-powered help</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Added Items */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Recently Added</h2>
              <p className="text-slate-600 text-sm">Fresh listings from our community</p>
            </div>
            <Link to="/browse?sort=newest" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <RecentlyAddedItems userCurrency={userCurrency} />
        </div>
      </section>

      {/* Flash Deals Section */}
      {flashDeals.length > 0 && (
        <section className="py-6 bg-gradient-to-r from-amber-50 via-orange-50 to-red-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-outfit text-xl font-bold flex items-center gap-2">
                  <span className="text-2xl">⚡</span> Flash Deals
                </h2>
                <p className="text-sm text-muted-foreground">Limited-time auctions & seller-marked deals — grab them before they're gone</p>
              </div>
              <Link to="/browse?flash=1" className="text-amber-700 hover:underline flex items-center gap-1 text-sm font-semibold">
                See all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="flash-deals-grid">
              {flashDeals.slice(0, 8).map((listing) => (
                <FlashDealCard key={listing.listing_id} listing={listing} userCurrency={userCurrency} navigate={navigate} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories Section - Show 8 categories */}
      <section className="py-6 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-lg font-bold">Browse Categories</h2>
            <Link to="/browse" className="text-emerald-600 hover:underline flex items-center gap-1 text-sm">
              View all ({categories.length}) <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {categories.slice(0, 8).map((cat) => (
              <CategoryCard 
                key={cat.id} 
                category={cat} 
                onClick={() => navigate(`/browse?category=${cat.id}`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Live Auctions + Ending Soon - Side by Side (only show if content) */}
      {featured.hot_auctions?.length > 0 && (
        <section className="py-6">
          <div className="container mx-auto px-4 md:px-6">
            <div className={`grid grid-cols-1 ${featured.ending_soon?.length > 0 ? 'lg:grid-cols-2' : ''} gap-6`}>
              {/* Live Auctions */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
                    <Gavel className="h-5 w-5 text-red-500" /> Live Auctions
                  </h2>
                  <Link to="/browse?type=auction" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
                    View all <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className={`grid grid-cols-2 ${featured.ending_soon?.length > 0 ? 'sm:grid-cols-3' : 'sm:grid-cols-4 lg:grid-cols-6'} gap-3`}>
                  {featured.hot_auctions.slice(0, featured.ending_soon?.length > 0 ? 12 : 18).map((listing) => (
                    <ProductCardCompact key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                  ))}
                </div>
              </div>

              {/* Ending Soon */}
              {featured.ending_soon?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-orange-500" /> Ending Soon
                    </h2>
                    <Link to="/browse?type=auction&sort=ending" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
                      View all <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {featured.ending_soon.slice(0, 12).map((listing) => (
                      <ProductCardCompact key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Buy Now Section (only show if items exist) */}
      {featured.latest_fixed?.length > 0 && (
        <section className="py-6 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
                <Tag className="h-5 w-5 text-emerald-500" /> Buy Now
              </h2>
              <Link to="/browse?type=fixed" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
                View all <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {featured.latest_fixed.slice(0, 18).map((listing) => (
                <ProductCardCompact key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Download App Banner */}
      <section className="py-6 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg">
                <img src="/bidzuu-icon-192.png" alt="BidZuu App" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Get the BidZuu App</h3>
                <p className="text-gray-400 text-sm">Shop anytime, anywhere. Download now!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href="https://apps.apple.com/us/app/bidzuu/id6752724318"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-white text-slate-900 hover:bg-gray-100 rounded-lg px-4">
                  <Apple className="h-5 w-5 mr-2" /> App Store
                </Button>
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.bidzuu.app"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-white text-slate-900 hover:bg-gray-100 rounded-lg px-4">
                  <Play className="h-5 w-5 mr-2" /> Google Play
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* New Listings + Best Deals - Side by Side */}
      <section className="py-6">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* New Listings */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" /> New Listings
                </h2>
                <Link to="/browse" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
                  View all <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              {featured.new_listings?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {featured.new_listings.slice(0, 12).map((listing) => (
                    <ProductCardCompact key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center bg-slate-50 border-dashed">
                  <Star className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <h3 className="font-semibold text-sm mb-1">No new listings</h3>
                  <Button onClick={() => navigate('/sell')} size="sm" variant="link" className="text-emerald-600">
                    Create Listing
                  </Button>
                </Card>
              )}
            </div>

            {/* Best Deals */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-500" /> Best Deals
                </h2>
                <Link to="/browse" className="text-emerald-600 hover:underline text-sm flex items-center gap-1">
                  View all <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
              {featured.best_deals?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {featured.best_deals.slice(0, 12).map((listing) => (
                    <ProductCardCompact key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center bg-slate-50 border-dashed">
                  <TrendingUp className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <h3 className="font-semibold text-sm mb-1">No deals yet</h3>
                  <Button onClick={() => navigate('/browse')} size="sm" variant="link" className="text-emerald-600">
                    Browse Items
                  </Button>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Join Community Banner */}
      <section className="py-6 bg-gradient-to-r from-emerald-500 to-teal-500">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Join the BidZuu Community</h3>
                <p className="text-white/80 text-sm">Connect with buyers & sellers, share finds, get tips!</p>
              </div>
            </div>
            <Button 
              className="bg-white text-emerald-600 hover:bg-gray-100 rounded-full px-6"
              onClick={() => navigate('/community')}
            >
              <MessageCircle className="h-5 w-5 mr-2" /> Join Now
            </Button>
          </div>
        </div>
      </section>

      {/* More Categories */}
      <section className="py-6 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-outfit text-lg font-bold">More Categories</h2>
            <Link to="/browse" className="text-emerald-600 hover:underline flex items-center gap-1 text-sm">
              View all <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {categories.slice(8, 16).map((cat) => (
              <CategoryCard 
                key={cat.id} 
                category={cat} 
                onClick={() => navigate(`/browse?category=${cat.id}`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Waitlist */}
      <section className="py-12 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left - CTA Text */}
            <div className="text-center lg:text-left">
              <h2 className="font-outfit text-2xl md:text-3xl font-bold mb-3">
                Ready to Join the Future of Resale?
              </h2>
              <p className="text-gray-400 mb-6 max-w-xl">
                Whether you're buying unique finds or selling to a global audience, BidZuu is the trusted platform for community-driven commerce.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => user ? navigate('/sell') : navigate('/auth')}
                >
                  Start Selling
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="rounded-full px-8 border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate('/browse')}
                >
                  Browse Items
                </Button>
              </div>
            </div>
            
            {/* Right - Waitlist Signup */}
            <div className="max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <WaitlistSignup />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
